import "./styles.scss";

import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import queryString from "query-string";
import { useTranslation } from "react-i18next";
import classnames from "classnames";

import useResponsive from "libs/helpers/useResponsive";

import { Main, Chat } from "./components";
import TaskOnboarding from "components/TaskOnboarding";
import FilesUpload from "components/FilesUpload";
import ScrollTopMobile from "components/ScrollTopMobile";
import { DeleteAttachmentModal } from "page_components/shared/tasks/modals";

const mainClass = "task-view";

const TaskView = ({
  data,
  website,
  loading,
  file_progress,
  updateTask,
  timeTracking,
  currentTimeTracking,
  sendMessage,
  editMessage,
  isMessageSending,
  isCompletelyDisabled,
}) => {
  const { t } = useTranslation();

  const { search, pathname } = useLocation();
  const query = queryString.parse(search);
  const navigate = useNavigate();
  const { max } = useResponsive();

  const [task_onboarding, setTaskOnboarding] = useState(false);
  const [attachment_delete, setAttachmentDelete] = useState(null);

  const description = useMemo(() => data?.messages?.[0], [data?.messages]);

  useEffect(() => {
    if (query?.onboarding === "task") {
      setTaskOnboarding(true);
    }
    // eslint-disable-next-line
  }, [query?.onboarding]);

  useEffect(() => {
    if (task_onboarding && max.sm) {
      const layoutBody = document.querySelector(
        ".dashboard-layout-content__body",
      );

      setTaskOnboarding(null);

      if (layoutBody) layoutBody.style.overflow = "";

      navigate(pathname, { replace: true });
    }
  }, [task_onboarding, navigate, pathname, max.sm]);

  useEffect(() => {
    if (!max.lg && !!attachment_delete) {
      setAttachmentDelete(null);
    }
  }, [attachment_delete, max.lg]);

  const onOnboardingClose = (response) => {
    const referrer = query?.referrer;

    window.history.replaceState({}, null, pathname);

    setTaskOnboarding(false);

    if (!!referrer && !response?.last) navigate(referrer);
  };

  return (
    <>
      <div
        className={classnames(mainClass, {
          [`${mainClass}--no-attachments`]:
            isCompletelyDisabled && !(description?.files?.length > 0),
        })}
      >
        <div id="task_info" className={`${mainClass}__column`}>
          <Main
            data={data}
            website={website}
            loading={loading}
            file_progress={file_progress}
            updateTask={updateTask}
            timeTracking={timeTracking}
            currentTimeTracking={currentTimeTracking}
            editMessage={editMessage}
            isCompletelyDisabled={isCompletelyDisabled}
          />
          {max.lg &&
            (isCompletelyDisabled ? description?.files?.length > 0 : true) && (
              <div className={`${mainClass}__attachments`}>
                <FilesUpload
                  layout={
                    description?.files?.length > 0 ? "columns" : "compact"
                  }
                  label={t("Attachments")}
                  description={t("Click or drag a file")}
                  serverFiles={description?.files ?? []}
                  progress={file_progress}
                  isCompletelyDisabled={isCompletelyDisabled}
                  onChange={(files) =>
                    editMessage(description._id, "KEEP_SAME", files, [])
                  }
                  onRemove={(file) =>
                    setAttachmentDelete({
                      id: description._id,
                      file,
                    })
                  }
                />
              </div>
            )}
        </div>
        <div id="task_chat" className={`${mainClass}__column`}>
          <Chat
            data={data}
            file_progress={file_progress}
            sendMessage={sendMessage}
            editMessage={editMessage}
            isMessageSending={isMessageSending}
            isCompletelyDisabled={isCompletelyDisabled}
          />
        </div>
        {!max.lg &&
          (isCompletelyDisabled ? description?.files?.length > 0 : true) && (
            <div
              className={`${mainClass}__column ${mainClass}__column--attachments`}
            >
              <FilesUpload
                layout={description?.files?.length > 0 ? "columns" : "compact"}
                label={t("Attachments")}
                description={t("Click or drag a file")}
                serverFiles={description?.files ?? []}
                progress={file_progress}
                isCompletelyDisabled={isCompletelyDisabled}
                onChange={(files) =>
                  editMessage(description._id, "KEEP_SAME", files, [])
                }
                onRemove={(file) =>
                  editMessage(description._id, "KEEP_SAME", [], [file.key])
                }
              />
            </div>
          )}
      </div>

      <div className={`${mainClass}-scroll`}>
        <ScrollTopMobile />
      </div>

      <DeleteAttachmentModal
        title={attachment_delete?.file?.name}
        isOpen={!!attachment_delete}
        onSubmit={async () =>
          editMessage(
            attachment_delete?.id,
            "KEEP_SAME",
            [],
            [attachment_delete?.file?.key],
          )
        }
        onClose={() => setAttachmentDelete(null)}
      />

      {!!task_onboarding && (
        <TaskOnboarding
          init_step={9}
          task_id={data?._id}
          task_status={data?.status}
          onClose={onOnboardingClose}
        />
      )}
    </>
  );
};

export default TaskView;
